import { useState, type FormEvent, type ReactNode } from 'react';
import { logout, setUser } from '../account/actions';
import { type AccountUser, ApiError, api } from '../account/api';
import { getLang, locale, t } from '../i18n/i18n';
import { store, useApp } from '../state/store';
import { IconClose } from './icons';

type View = 'login' | 'register' | 'verify' | 'forgot' | 'reset';

const ROLE_NAMES = { user: 'User', admin: 'Administrator', superadmin: 'Superadmin' };

/** Log in, create an account (confirmed with a code by email), reset the password, or the account page. */
export function AccountDialog() {
  const user = useApp((s) => s.account.user);
  const close = () => store.set({ panel: null });
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="account-title" onKeyDown={(e) => e.key === 'Escape' && close()}>
      <div className="modal narrow" style={{ width: 'min(440px, 100%)' }}>
        {user ? <Profile user={user} close={close} /> : <SignIn close={close} />}
      </div>
    </div>
  );
}

function Head({ title, close }: { title: string; close: () => void }) {
  return (
    <div className="modal-head">
      <h2 id="account-title">{title}</h2>
      <button className="btn icon ghost" aria-label={t('Close dialog')} onClick={close}>
        <IconClose />
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field" style={{ marginBottom: 10 }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function useBusy() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await f();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

function SignIn({ close }: { close: () => void }) {
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [info, setInfo] = useState<string | null>(null);
  const { busy, error, setError, run } = useBusy();

  const go = (v: View, message: string | null = null) => {
    setView(v);
    setError(null);
    setInfo(message);
    setCode('');
  };
  const loggedIn = (u: AccountUser) => {
    setUser(u);
    close();
    store.toast('success', t('Logged in as {email}.', { email: u.email }));
  };
  const submit = (f: () => Promise<void>) => (e: FormEvent) => {
    e.preventDefault();
    void run(f);
  };

  const titles: Record<View, string> = {
    login: t('Log in'),
    register: t('Create an account'),
    verify: t('Confirm your email address'),
    forgot: t('Forgot password'),
    reset: t('Choose a new password'),
  };

  const emailField = (
    <Field label={t('Email')}>
      <input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
    </Field>
  );
  const codeField = (
    <Field label={t('Code from the email')}>
      <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
    </Field>
  );

  return (
    <>
      <Head title={titles[view]} close={close} />
      <form
        className="modal-body"
        onSubmit={submit(async () => {
          if (view === 'login') {
            try {
              loggedIn((await api<{ user: AccountUser }>('POST', '/api/auth/login', { email, password })).user);
            } catch (e) {
              if (e instanceof ApiError && e.code === 'email_not_verified') {
                await api('POST', '/api/auth/resend', { email, purpose: 'verify' });
                go('verify', t('Your email address is not confirmed yet. We have sent a new code to {email}.', { email }));
                return;
              }
              throw e;
            }
          } else if (view === 'register') {
            await api('POST', '/api/auth/register', { email, password, lang: getLang() });
            go('verify', t('We have sent a code to {email}. If the address already has an account, log in instead.', { email }));
          } else if (view === 'verify') {
            loggedIn((await api<{ user: AccountUser }>('POST', '/api/auth/verify', { email, password, code })).user);
          } else if (view === 'forgot') {
            await api('POST', '/api/auth/forgot', { email });
            go('reset', t('If {email} has an account, we have sent a code to it.', { email }));
            setPassword('');
          } else {
            loggedIn((await api<{ user: AccountUser }>('POST', '/api/auth/reset', { email, code, password })).user);
          }
        })}
      >
        {info && <p className="issue info">{info}</p>}
        {view === 'login' && (
          <>
            {emailField}
            <Field label={t('Password')}>
              <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          </>
        )}
        {view === 'register' && (
          <>
            <p className="hint" style={{ marginTop: 0 }}>{t('With an account you can store projects in the cloud and open them on other devices. Without one, StitchCraft works as before, in this browser only.')}</p>
            {emailField}
            <Field label={t('Password (at least {n} characters)', { n: 10 })}>
              <input className="input" type="password" autoComplete="new-password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          </>
        )}
        {view === 'verify' && codeField}
        {view === 'forgot' && emailField}
        {view === 'reset' && (
          <>
            {codeField}
            <Field label={t('New password (at least {n} characters)', { n: 10 })}>
              <input className="input" type="password" autoComplete="new-password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          </>
        )}
        {error && (
          <p className="issue error" role="alert">
            {error}
          </p>
        )}
        <div className="row wrap" style={{ marginTop: 6 }}>
          <button className="btn primary" type="submit" disabled={busy}>
            {{ login: t('Log in'), register: t('Create account'), verify: t('Confirm'), forgot: t('Send code'), reset: t('Save new password') }[view]}
          </button>
          <span className="spacer" />
          {view === 'login' && (
            <>
              <button className="btn ghost" type="button" onClick={() => go('register')}>
                {t('Create an account')}
              </button>
              <button className="btn ghost" type="button" onClick={() => go('forgot')}>
                {t('Forgot password?')}
              </button>
            </>
          )}
          {(view === 'verify' || view === 'reset') && (
            <button
              className="btn ghost"
              type="button"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api('POST', '/api/auth/resend', { email, purpose: view === 'reset' ? 'reset' : 'verify' });
                  setInfo(t('A new code is on its way to {email}. You can ask for a new code once a minute.', { email }));
                })
              }
            >
              {t('Send a new code')}
            </button>
          )}
          {view !== 'login' && (
            <button className="btn ghost" type="button" onClick={() => go('login')}>
              {t('Back to log in')}
            </button>
          )}
        </div>
      </form>
    </>
  );
}

function Profile({ user, close }: { user: AccountUser; close: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [changed, setChanged] = useState(false);
  const { busy, error, run } = useBusy();
  const isAdmin = user.role === 'admin' || user.role === 'superadmin';
  return (
    <>
      <Head title={t('Account')} close={close} />
      <div className="modal-body">
        <dl className="kv">
          <dt>{t('Email')}</dt>
          <dd>{user.email}</dd>
          <dt>{t('Role')}</dt>
          <dd>{t(ROLE_NAMES[user.role])}</dd>
          <dt>{t('Member since')}</dt>
          <dd>{new Date(user.createdAt).toLocaleDateString(locale())}</dd>
        </dl>
        <p className="hint">{t('While you are logged in, everything you save is stored in the cloud and can be opened on all your devices.')}</p>
        <form
          className="section"
          style={{ padding: '12px 0', borderBottom: 0 }}
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await api('POST', '/api/auth/password', { current, password: next });
              setCurrent('');
              setNext('');
              setChanged(true);
            });
          }}
        >
          <h3>{t('Change password')}</h3>
          <div className="grid2">
            <Field label={t('Current password')}>
              <input className="input" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
            </Field>
            <Field label={t('New password')}>
              <input className="input" type="password" autoComplete="new-password" minLength={10} required value={next} onChange={(e) => setNext(e.target.value)} />
            </Field>
          </div>
          {changed && <p className="issue info">{t('The password is changed. Other devices are logged out.')}</p>}
          {error && (
            <p className="issue error" role="alert">
              {error}
            </p>
          )}
          <button className="btn" type="submit" disabled={busy}>
            {t('Change password')}
          </button>
        </form>
      </div>
      <div className="modal-foot">
        {isAdmin && (
          <button className="btn" onClick={() => store.set({ panel: 'admin' })}>
            {t('Administration')}
          </button>
        )}
        <span className="spacer" />
        <button
          className="btn"
          onClick={() =>
            void run(async () => {
              await logout();
              close();
            })
          }
        >
          {t('Log out')}
        </button>
      </div>
    </>
  );
}

export { ROLE_NAMES };
